import { IS_LOCAL, environment } from "@config";
import type { OverridedMixpanel } from "mixpanel-browser";
import { ErrorService } from "./ErrorService";

// Mixpanel is on the page purely to capture Session Replays - analytics events
// still go out through Segment (see Analytics.ts), which is why every identity
// call here mirrors what Segment already knows rather than deriving its own.
//
// This mirrors future-money-mobile/src/services/SessionReplay.ts so both
// platforms record under the same rules.
//
// The SDK is imported dynamically: it is ~430kB unminified and nothing about
// replay capture needs to happen before first paint, so it stays out of the
// entry chunk. `ready` is what every method waits on, and doubles as the
// in-flight guard for StrictMode's double-invoked bootstrap effect.
let mixpanel: OverridedMixpanel | null = null;
let ready: Promise<void> | null = null;

const enabled = () =>
  Boolean(environment.mixpanel.token) && (!IS_LOCAL || environment.mixpanel.recordLocally);

const init = () => {
  if (!enabled()) return Promise.resolve();
  if (ready) return ready;

  ready = import("mixpanel-browser")
    .then(({ default: sdk }) => {
      sdk.init(environment.mixpanel.token, {
        record_sessions_percent: environment.mixpanel.recordSessionsPercent,
        // Turned on by bindDeviceId once Segment's anonymousId is registered.
        // Enabling it here lets heatmap clicks fire against the $device_id
        // Mixpanel generates for itself, which orphans anonymous profiles and
        // inflates the MTU count before the Segment binding lands.
        record_heatmap_data: false,
      });

      mixpanel = sdk;
    })
    // An ad blocker or a failed chunk fetch must not take analytics down with
    // it - every method below no-ops while `mixpanel` stays null.
    .catch((error) => ErrorService.capture(error));

  return ready;
};

// Segment owns identity on web, so Mixpanel is pinned to Segment's anonymousId
// instead of generating its own. Original ID Merge - distinct_id matches
// $device_id - which is how mobile initializes MPSessionReplay.
const bindDeviceId = async (anonymousId: string) => {
  await ready;
  if (!mixpanel) return;

  mixpanel.register({ $device_id: anonymousId, distinct_id: anonymousId });
  mixpanel.set_config({ record_heatmap_data: true });
};

const identify = async (userId: string) => {
  await ready;
  if (!mixpanel) return;

  mixpanel.identify(userId);
};

// Drops the previous user's distinct_id so the next session on this browser is
// not stitched onto the account that just signed out.
const reset = async () => {
  await ready;
  if (!mixpanel) return;

  mixpanel.reset();
};

const startRecording = async () => {
  await ready;
  if (!mixpanel) return;

  mixpanel.start_session_recording();
};

const stopRecording = async () => {
  await ready;
  if (!mixpanel) return;

  mixpanel.stop_session_recording();
};

// The $mp_replay_id of the capture in progress, or an empty object when there
// is none - the shape the Segment middleware spreads onto every payload. Stays
// synchronous because middleware runs inline on every event.
const getReplayProperties = () => mixpanel?.get_session_recording_properties() ?? {};

export const SessionReplay = {
  init,
  bindDeviceId,
  identify,
  reset,
  startRecording,
  stopRecording,
  getReplayProperties,
};
